// The Airwallex card form's shape, as one description both renderers read.
//
// The buyer's checkout draws this form twice: once for real, mounting one
// Airwallex iframe per field (`AirwallexCardPane`), and once inert, inside the
// merchant dashboard's live preview (`PreviewPaymentSurface`). The preview
// cannot be the real one — mounting a processor's SDK in a dashboard frame is
// a payment nobody asked to start, and `check-preview-inert.mjs` exists to
// forbid it — so there are necessarily two renderers of the same form.
//
// Two renderers of one form is the drift hazard this file exists to remove. A
// preview that shows a layout the pane does not produce is worse than no
// preview at all, because the merchant configures against it and finds out
// from a buyer. So neither renderer describes the geometry: both read
// `AIRWALLEX_CARD_LAYOUTS`, and a preset that exists in one and not the other
// is a TYPE ERROR here rather than a guard finding later — `Record<
// AirwallexCardPreset, …>` has to name every member of the union.
//
// What lives here is geometry and nothing else. Colours, radius, padding and
// type come from the merchant's own branding tokens, and what happens INSIDE
// each box belongs to Airwallex.

/**
 * The card layouts the checkout can draw.
 *
 * Presentation only. Every one of them collects the card inside Airwallex's
 * own iframes and confirms the same intent, so the PCI scope, the guards and
 * the return leg are identical whichever the merchant picks — which is what
 * makes this a setting rather than a second rail.
 *
 * `fullFeaturedCard`, Airwallex's own all-in-one element, is deliberately not
 * on this list. It is a different element with a different theming API, and it
 * takes the layout away from us entirely; it needs its own decision, not a
 * seventh row in a list of geometries.
 */
export const AIRWALLEX_CARD_PRESETS = [
  'single_row',
  'split_fields',
  'split_fields_labelled',
  'stacked',
  'number_expiry_row',
  'compact',
] as const;

export type AirwallexCardPreset = (typeof AIRWALLEX_CARD_PRESETS)[number];

/**
 * A field of the card form, named as Airwallex's `createElement` names it.
 *
 * `card` is the combined element — number, expiry and CVC in one iframe on one
 * line — and it never appears alongside the other three: a layout is either
 * the one element or the trio.
 */
export type AirwallexCardField = 'card' | 'cardNumber' | 'expiry' | 'cvc';

export interface AirwallexCardLayout {
  /**
   * The elements to create, in creation order.
   *
   * Order is load-bearing twice over: `confirm()` is called on the first one
   * (the combined element, or the number field, which coordinates its
   * siblings), and completion advances the caret to the next.
   */
  readonly elements: readonly AirwallexCardField[];
  /**
   * The rows to draw, top to bottom, each naming the fields on it left to
   * right. Fields sharing a row share its width evenly.
   *
   * Every field in `elements` appears exactly once across `rows`, and nothing
   * else does — `airwallexCardLayoutIsCoherent` is that claim, executable.
   */
  readonly rows: readonly (readonly AirwallexCardField[])[];
  /**
   * How a row's width is shared, when sharing it evenly is wrong.
   *
   * **Optional, and absent is the normal case**: a row with no weights splits
   * evenly, which is what every other preset wants — `stacked` is full-width
   * rows, `split_fields`, `split_fields_labelled` and `compact` pair expiry
   * and CVC, which are both short and genuinely want half each, and
   * `single_row` is one element. Only `number_expiry_row` puts two fields of
   * very different lengths on one line, and only it declares weights.
   *
   * Declared as weights rather than widths on purpose. A fixed width is right
   * for exactly the embed width it was tuned against and wrong either side of
   * it; a weight is a ratio and holds at any width. That is not hypothetical
   * — the defect that produced this field was a card-number placeholder
   * clipped at 460px, which no fixed width would have fixed for the merchant
   * whose embed is 380px.
   *
   * A field with no entry weighs 1.
   */
  readonly weights?: Readonly<Partial<Record<AirwallexCardField, number>>>;
  /**
   * Whether this preset captions its fields by default.
   *
   * The merchant can override it (`branding.airwallexLabelStyle`); this is
   * what the preset means when they have not. Captions are always visual: see
   * `AIRWALLEX_LABEL_STYLES`.
   */
  readonly labels: boolean;
  /**
   * `compact` draws one step tighter and one step smaller than the merchant's
   * own `inputSize` and field type size, rather than replacing them — a
   * merchant who chose spacious spacing and this preset gets spacious-minus-
   * one, not the same form as a merchant who chose compact.
   */
  readonly density: 'default' | 'compact';
}

const SPLIT: readonly AirwallexCardField[] = ['cardNumber', 'expiry', 'cvc'];

/**
 * Every preset's geometry, and the single source of it.
 *
 * Typed as a total `Record` on purpose. Adding a preset to
 * `AIRWALLEX_CARD_PRESETS` without adding its geometry here does not compile,
 * so the two cannot come apart in the direction that would leave a renderer
 * guessing.
 */
export const AIRWALLEX_CARD_LAYOUTS: Record<AirwallexCardPreset, AirwallexCardLayout> = {
  // Airwallex's combined element. One iframe, one row, and the only preset
  // whose insides are not ours to lay out — which is why it carries no
  // captions: there is nothing to caption but the row as a whole.
  single_row: {
    elements: ['card'],
    rows: [['card']],
    labels: false,
    density: 'default',
  },
  // The trio with placeholders carrying the meaning. Shorter than the
  // labelled variant and fine on a wide surface.
  split_fields: {
    elements: SPLIT,
    rows: [['cardNumber'], ['expiry', 'cvc']],
    labels: false,
    density: 'default',
  },
  // The default. The same geometry, captioned — which is what makes it
  // survive a narrow embed, where a bare field row stops being self-evident.
  split_fields_labelled: {
    elements: SPLIT,
    rows: [['cardNumber'], ['expiry', 'cvc']],
    labels: true,
    density: 'default',
  },
  // Every field full width. Expiry and CVC side by side start to crowd below
  // roughly 320px, and this is the layout that does not.
  stacked: {
    elements: SPLIT,
    rows: [['cardNumber'], ['expiry'], ['cvc']],
    labels: true,
    density: 'default',
  },
  // Two rows instead of two-plus-one: shorter than the split forms without
  // the ambiguity of a single combined field.
  //
  // 3:2, not an even split. A card number is nineteen characters and an expiry
  // is five, so halving the row clipped the number's own placeholder at 460px
  // — seen in the preview before this was weighted. The proposal drew it even;
  // the merchant-facing decision was taken on the two drawn side by side and
  // the weighted one was chosen, so do not "restore" the even split to match
  // the original mock.
  number_expiry_row: {
    elements: SPLIT,
    rows: [['cardNumber', 'expiry'], ['cvc']],
    weights: { cardNumber: 3, expiry: 2 },
    labels: true,
    density: 'default',
  },
  // Split geometry at reduced density and type size, for a checkout embedded
  // in a page that already has its own chrome.
  compact: {
    elements: SPLIT,
    rows: [['cardNumber'], ['expiry', 'cvc']],
    labels: false,
    density: 'compact',
  },
};

/**
 * The preset that renders when nobody has chosen one.
 *
 * Not `DEFAULT_BRANDING.airwallexCardPreset`, which is the empty string: the
 * branding field means "the merchant's choice, in the shop's checkout
 * customizer", and empty means they have not made one. This is the last step
 * of the resolution, after the connector account's own setting.
 * See `resolveAirwallexCardPreset`.
 */
export const AIRWALLEX_CARD_PRESET_FALLBACK: AirwallexCardPreset = 'split_fields_labelled';

/**
 * Which preset the buyer's card form is drawn from.
 *
 * Three sources, in this order, and the order is the migration story:
 *
 *  1. **The shop's branding.** Set in the checkout customizer, empty when the
 *     merchant has not been there. Empty rather than pre-filled with the
 *     default precisely so that (2) still means something — a branding field
 *     that always held a value would silently override every connector
 *     account already configured, which is a rendering change no merchant
 *     asked for.
 *  2. **The connector account's `integration_style` metadata**, where this
 *     setting lived before the customizer existed, projected onto the checkout
 *     payload as `card_connector_integration_style`. Still authoritative for
 *     every merchant who set it and has not overridden it here. Nothing
 *     migrates it; it simply keeps working.
 *  3. **`split_fields_labelled`**, for a shop that has never set either.
 *
 * Step 3 is the one visible change: a shop with nothing configured used to get
 * the combined single row, and now gets the labelled split form.
 *
 * Anything unrecognised at either step falls through rather than failing. The
 * dashboard and the router deploy separately and may already offer a preset
 * this build has not heard of; a card form in the wrong layout is a cosmetic
 * disappointment, and no card form is a lost sale.
 */
export function resolveAirwallexCardPreset(
  fromBranding: string | null | undefined,
  fromConnector: string | null | undefined,
): AirwallexCardPreset {
  return asPreset(fromBranding) ?? asPreset(fromConnector) ?? AIRWALLEX_CARD_PRESET_FALLBACK;
}

/** The preset named, or `null` for anything this build does not know. */
export function asAirwallexCardPreset(value: unknown): AirwallexCardPreset | null {
  return asPreset(value);
}

function asPreset(value: unknown): AirwallexCardPreset | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim().toLowerCase();
  return (AIRWALLEX_CARD_PRESETS as readonly string[]).includes(trimmed)
    ? (trimmed as AirwallexCardPreset)
    : null;
}

/** The geometry for a preset. Total, by the type of `AIRWALLEX_CARD_LAYOUTS`. */
export function airwallexCardLayout(preset: AirwallexCardPreset): AirwallexCardLayout {
  return AIRWALLEX_CARD_LAYOUTS[preset];
}

/**
 * How much of its row a field takes, relative to its neighbours.
 *
 * `1` unless the layout says otherwise, so a caller can multiply blindly and
 * an unweighted row divides evenly — which is what the CSS did before weights
 * existed, and therefore what every preset but one still gets.
 */
export function airwallexCellWeight(
  layout: AirwallexCardLayout,
  field: AirwallexCardField,
): number {
  const weight = layout.weights?.[field];
  return typeof weight === 'number' && Number.isFinite(weight) && weight > 0 ? weight : 1;
}

/**
 * Whether a layout's rows draw exactly the elements it creates.
 *
 * The one thing the `Record` type cannot check. An element created with no row
 * to mount into leaves its field permanently pending — the pane's own comment
 * for that case is "the form would sit on the skeleton until the deadline with
 * no reason given" — and a row naming a field with no element draws an empty
 * box the buyer can click into and never type in. Both are silent.
 *
 * Executed by the checkout's preview guard over every preset, which is where a
 * new one gets caught.
 */
export function airwallexCardLayoutIsCoherent(layout: AirwallexCardLayout): boolean {
  const drawn = layout.rows.flat();
  if (drawn.length !== layout.elements.length) return false;
  return layout.elements.every((field) => drawn.filter((f) => f === field).length === 1);
}

/**
 * How the field's caption is drawn.
 *
 * Every one of these is **visual only**, and that is not a limitation we chose:
 * `for` cannot cross into a cross-origin iframe, so a `<label>` here would name
 * nothing and focus nothing. The field keeps the accessible name Airwallex
 * gives it inside the frame, and these captions sit beside it as an
 * affordance. The pane ships a caption on exactly these terms today.
 *
 * `''` is not a style — it means "whatever the preset says", so that
 * `split_fields` and `split_fields_labelled` keep meaning what they mean while
 * a merchant can still put floating captions on `compact`.
 */
export const AIRWALLEX_LABEL_STYLES = ['above', 'inline', 'floating', 'none'] as const;
export type AirwallexLabelStyle = (typeof AIRWALLEX_LABEL_STYLES)[number];

/**
 * The caption style actually drawn: the merchant's override, or the preset's
 * own answer to whether it captions at all.
 */
export function resolveAirwallexLabelStyle(
  override: string | null | undefined,
  layout: AirwallexCardLayout,
): AirwallexLabelStyle {
  if (
    typeof override === 'string' &&
    (AIRWALLEX_LABEL_STYLES as readonly string[]).includes(override)
  ) {
    return override as AirwallexLabelStyle;
  }
  return layout.labels ? 'above' : 'none';
}

/** How the box around each field is drawn. All three are the pane's own CSS. */
export const AIRWALLEX_FIELD_CHROMES = ['outlined', 'filled', 'underline'] as const;
export type AirwallexFieldChrome = (typeof AIRWALLEX_FIELD_CHROMES)[number];

/**
 * The ring drawn around the focused field.
 *
 * Half of it is the pane's CSS on the container and half is Airwallex's
 * `:focus` bucket inside the frame; they are one control because a ring that
 * appeared on only one side of the iframe boundary would look like a bug.
 */
export const AIRWALLEX_FOCUS_RINGS = ['none', 'subtle', 'strong'] as const;
export type AirwallexFocusRing = (typeof AIRWALLEX_FOCUS_RINGS)[number];

/**
 * What the browser's autofill highlight is allowed to do to the field.
 *
 * The one control on this list with no workaround from outside the frame:
 * the browser paints its own background on an autofilled input, and only the
 * SDK's `:autofill` bucket reaches it. `system` leaves it alone — the buyer's
 * own browser telling them it filled the field is information, not a style
 * bug — which is why it is the default.
 */
export const AIRWALLEX_AUTOFILL_TREATMENTS = ['system', 'brand', 'neutral'] as const;
export type AirwallexAutofillTreatment = (typeof AIRWALLEX_AUTOFILL_TREATMENTS)[number];

/** Field type sizes, as the checkout's other size scales are named. */
export const AIRWALLEX_FIELD_FONT_SIZES = ['sm', 'md', 'lg'] as const;
export type AirwallexFieldFontSize = (typeof AIRWALLEX_FIELD_FONT_SIZES)[number];

/** Field type weights. `medium` is what the pane hardcoded before this existed. */
export const AIRWALLEX_FIELD_WEIGHTS = ['regular', 'medium', 'semibold'] as const;
export type AirwallexFieldWeight = (typeof AIRWALLEX_FIELD_WEIGHTS)[number];

/** Field type size in px, for the SDK's `base.fontSize`. */
export function airwallexFieldFontPx(
  size: AirwallexFieldFontSize,
  density: AirwallexCardLayout['density'],
): number {
  const base = size === 'sm' ? 14 : size === 'lg' ? 18 : 16;
  // A compact preset draws one step smaller, floored so the field never
  // reaches the size at which iOS zooms the page on focus minus two.
  return density === 'compact' ? Math.max(13, base - 1) : base;
}

/** Field type weight as a CSS numeric weight, for the SDK's `base.fontWeight`. */
export function airwallexFieldWeightValue(weight: AirwallexFieldWeight): number {
  return weight === 'regular' ? 400 : weight === 'semibold' ? 600 : 500;
}

/**
 * The card networks whose marks the field row can display.
 *
 * **Decorative.** This list does not restrict what Airwallex accepts — that
 * would be `allowedCardNetworks`, which this build deliberately does not set,
 * because narrowing the networks a buyer may pay with is an acceptance change
 * and not a styling one. So the marks say what the merchant claims to take,
 * and it is the merchant's job to keep that true; the dashboard's field hint
 * says so in as many words. Enforcement is its own decision.
 */
export const AIRWALLEX_NETWORK_MARKS = ['visa', 'mastercard', 'amex', 'discover'] as const;
export type AirwallexNetworkMark = (typeof AIRWALLEX_NETWORK_MARKS)[number];
